import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Typography, Alert, Spin, Tag, Space, Table, Button, Tooltip as AntTooltip, theme as antdTheme } from 'antd';
import {
  UnorderedListOutlined, TeamOutlined, MailOutlined, SendOutlined,
  EyeOutlined, ArrowUpOutlined, ExclamationCircleOutlined, BarChartOutlined,
  CheckCircleOutlined, WarningOutlined, SafetyOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LineChart, Line,
} from 'recharts';
import axios from 'axios';

const { Title, Text } = Typography;

const STATUS_COLOR = { draft: '#94a3b8', sending: '#f59e0b', completed: '#9DC533', scheduled: '#6366f1' };
const STATUS_TAG = { draft: 'default', sending: 'warning', completed: 'success', scheduled: 'processing' };

const StatCard = ({ title, value, icon, iconBg, iconColor, suffix }) => {
  const { token } = antdTheme.useToken();
  return (
    <Card style={{ borderRadius: 14, border: `1px solid ${token.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <Text style={{ color: token.colorTextSecondary, fontSize: 13, fontWeight: 500 }}>{title}</Text>
          <div style={{ fontSize: 34, fontWeight: 800, color: token.colorText, lineHeight: 1.1, marginTop: 6 }}>
            {value}
            {suffix && <span style={{ fontSize: 16, color: token.colorTextSecondary, marginLeft: 4 }}>{suffix}</span>}
          </div>
        </div>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { style: { fontSize: 22, color: iconColor } })}
        </div>
      </div>
    </Card>
  );
};

const Dashboard = () => {
  const { token } = antdTheme.useToken();
  const [stats,          setStats]          = useState(null);
  const [campaigns,      setCampaigns]      = useState([]);
  const [campaignStats,  setCampaignStats]  = useState({});  // id → stats
  const [health,         setHealth]         = useState(null);
  const [authStatus,     setAuthStatus]     = useState(null);
  const [reputation,     setReputation]     = useState(null);
  const [error,          setError]          = useState('');
  const [loading,        setLoading]        = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [s, c, h, a, rep] = await Promise.all([
          axios.get('/api/stats'),
          axios.get('/api/campaigns'),
          axios.get('/api/stats/health').catch(() => null),
          axios.get('/api/deliverability/auth-status').catch(() => null),
          axios.get('/api/deliverability/reputation').catch(() => null),
        ]);
        setStats(s.data);
        if (h?.data) setHealth(h.data);
        if (a?.data) setAuthStatus(a.data);
        if (rep?.data) setReputation(rep.data);
        const recent = (c.data.data ?? c.data).slice(0, 5);
        setCampaigns(recent);

        // Fetch per-campaign stats for non-draft campaigns
        const nonDraft = recent.filter(camp => camp.status !== 'draft');
        if (nonDraft.length > 0) {
          const results = await Promise.allSettled(
            nonDraft.map(camp => axios.get(`/api/campaigns/${camp.id}/stats`))
          );
          const statsMap = {};
          nonDraft.forEach((camp, i) => {
            if (results[i].status === 'fulfilled') statsMap[camp.id] = results[i].value.data;
          });
          setCampaignStats(statsMap);
        }
      } catch {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
      <Spin size="large" />
    </div>
  );

  const chartData = campaigns.slice(0, 8).map(c => ({
    name: c.name.length > 12 ? c.name.slice(0, 12) + '…' : c.name,
    status: c.status,
    value: 1,
  }));

  const Pill = ({ value, color, bg, label }) => (
    <AntTooltip title={label}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        background: bg, color, borderRadius: 20,
        padding: '2px 8px', fontSize: 11, fontWeight: 700,
        whiteSpace: 'nowrap',
      }}>
        {value}
      </span>
    </AntTooltip>
  );

  const recentColumns = [
    {
      title: 'Campaign', dataIndex: 'name', width: 200,
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text strong style={{ flex: 1, fontSize: 13 }}>{name}</Text>
          {record.status !== 'draft' && (
            <AntTooltip title="View analytics">
              <Button
                type="text" size="small" icon={<BarChartOutlined />}
                onClick={() => navigate(`/campaigns/${record.id}/report`)}
                style={{ color: token.colorPrimary, padding: '0 4px', height: 22, flexShrink: 0 }}
              />
            </AntTooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: s => <Tag color={STATUS_TAG[s]} style={{ fontSize: 11 }}>{s.charAt(0).toUpperCase() + s.slice(1)}</Tag>,
    },
    {
      title: 'Sent', key: 'sent', width: 70, align: 'center',
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <Text style={{ color: token.colorTextTertiary, fontSize: 12 }}>—</Text>;
        return <Pill value={s.total} color={token.colorText} bg={token.colorBgLayout} label="Total emails sent" />;
      },
    },
    {
      title: 'Open Rate', key: 'openRate', width: 95, align: 'center',
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <Text style={{ color: token.colorTextTertiary, fontSize: 12 }}>—</Text>;
        return <Pill value={`${s.openRate}%`} color="#7EA028" bg="#EEF6D0" label={`${s.opened + s.clicked} opened`} />;
      },
    },
    {
      title: 'Click Rate', key: 'clickRate', width: 95, align: 'center',
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <Text style={{ color: token.colorTextTertiary, fontSize: 12 }}>—</Text>;
        return <Pill value={`${s.clickRate}%`} color="#2563eb" bg="#dbeafe" label={`${s.clicked} clicked`} />;
      },
    },
    {
      title: 'Bounce Rate', key: 'bounceRate', width: 105, align: 'center',
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <Text style={{ color: token.colorTextTertiary, fontSize: 12 }}>—</Text>;
        return <Pill value={`${s.bounceRate}%`} color="#dc2626" bg="#fef2f2" label={`${s.bounced} bounced`} />;
      },
    },
    {
      title: 'Complaint Rate', key: 'complaintRate', width: 110, align: 'center', responsive: ['lg'],
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <Text style={{ color: token.colorTextTertiary, fontSize: 12 }}>—</Text>;
        return <Pill value={`${s.complaintRate ?? '0'}%`} color="#b91c1c" bg="#fee2e2" label={`${s.complained ?? 0} complaints`} />;
      },
    },
    {
      title: 'Unsub Rate', key: 'unsubscribeRate', width: 105, align: 'center', responsive: ['lg'],
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <Text style={{ color: token.colorTextTertiary, fontSize: 12 }}>—</Text>;
        return <Pill value={`${s.unsubscribeRate ?? '0'}%`} color="#6b7280" bg="#f3f4f6" label={`${s.unsubscribed ?? 0} unsubscribed`} />;
      },
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 110,
      render: d => <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <Title level={3} style={{ margin: 0, color: token.colorText }}>Dashboard</Title>
        <Text style={{ color: token.colorTextSecondary }}>Overview of your email marketing activity</Text>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20, borderRadius: 10 }} />}

      {/* Duplicate-send health check — early warning signal */}
      {health && (
        health.ok ? (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Delivery health: no duplicate sends detected"
            description="Every recipient received each campaign at most once."
            style={{ marginBottom: 20, borderRadius: 10 }}
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={`Duplicate sends detected: ${health.duplicateRecipients} recipient${health.duplicateRecipients === 1 ? '' : 's'} received a campaign more than once (${health.extraSends} extra email${health.extraSends === 1 ? '' : 's'})`}
            description={
              <div>
                <Text style={{ fontSize: 13 }}>Affected campaigns:</Text>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {health.campaigns.slice(0, 5).map(c => (
                    <li key={c.id} style={{ fontSize: 13 }}>
                      <Text strong>{c.name}</Text> — {c.duplicatedRecipients} recipient(s), {c.extraSends} extra send(s)
                    </li>
                  ))}
                </ul>
              </div>
            }
            style={{ marginBottom: 20, borderRadius: 10 }}
          />
        )
      )}

      {/* Stat Cards */}
      <Row gutter={[20, 20]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard title="Total Lists" value={stats?.lists ?? 0} icon={<UnorderedListOutlined />} iconBg="#ede9fe" iconColor="#6366f1" />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard title="Subscribers" value={stats?.subscribers ?? 0} icon={<TeamOutlined />} iconBg="#EEF6D0" iconColor="#9DC533" />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard title="Campaigns" value={stats?.campaigns ?? 0} icon={<MailOutlined />} iconBg="#fef3c7" iconColor="#f59e0b" />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard title="Emails Sent" value={stats?.emailsSent ?? 0} icon={<SendOutlined />} iconBg="#dbeafe" iconColor="#3b82f6" />
        </Col>
      </Row>

      {/* Engagement Row */}
      {stats && (stats.opens > 0 || stats.clicks > 0 || stats.bounces > 0) && (
        <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
          <Col xs={24} sm={8}>
            <StatCard title="Total Opens" value={stats.opens} icon={<EyeOutlined />} iconBg="#f0fdf4" iconColor="#16a34a" />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard title="Total Clicks" value={stats.clicks} icon={<ArrowUpOutlined />} iconBg="#eff6ff" iconColor="#2563eb" />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard title="Bounces" value={stats.bounces} icon={<ExclamationCircleOutlined />} iconBg="#fef2f2" iconColor="#dc2626" />
          </Col>
        </Row>
      )}

      {/* ── Deliverability Health ── */}
      {(authStatus || reputation) && (
        <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
          {/* Authentication */}
          {authStatus && (
            <Col xs={24} lg={10}>
              <Card style={{ borderRadius: 14, border: `1px solid ${token.colorBorderSecondary}`, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <SafetyOutlined style={{ color: '#6366f1' }} />
                  <Text strong>Email Authentication</Text>
                  <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>· {authStatus.domain}</Text>
                </div>
                {[
                  { key: 'DKIM', d: authStatus.dkim },
                  { key: 'SPF', d: authStatus.spf },
                  { key: 'DMARC', d: authStatus.dmarc },
                ].map(({ key, d }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                    {d?.pass
                      ? <CheckCircleOutlined style={{ color: '#16a34a', marginTop: 2 }} />
                      : <CloseCircleOutlined style={{ color: '#dc2626', marginTop: 2 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong style={{ fontSize: 13 }}>{key}</Text>
                        <Tag color={d?.pass ? 'success' : 'error'} style={{ margin: 0, fontSize: 11 }}>
                          {d?.pass ? 'Pass' : 'Missing'}
                        </Tag>
                      </div>
                      {key === 'DMARC' && d?.policy && <Text style={{ fontSize: 11, color: token.colorTextTertiary }}>policy: p={d.policy}</Text>}
                      {d?.note && <div><Text style={{ fontSize: 11, color: '#d97706' }}>{d.note}</Text></div>}
                    </div>
                  </div>
                ))}
                {authStatus.allPass && (
                  <Text style={{ fontSize: 12, color: '#16a34a', display: 'block', marginTop: 10 }}>
                    ✓ All authentication checks pass — you're set up correctly.
                  </Text>
                )}
              </Card>
            </Col>
          )}

          {/* Reputation */}
          {reputation && (
            <Col xs={24} lg={14}>
              <Card style={{ borderRadius: 14, border: `1px solid ${token.colorBorderSecondary}`, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <BarChartOutlined style={{ color: '#6366f1' }} />
                  <Text strong>Sender Health</Text>
                  <Tag
                    color={reputation.health === 'good' ? 'success' : reputation.health === 'warning' ? 'warning' : 'error'}
                    style={{ margin: 0, fontSize: 11 }}
                  >
                    {reputation.health === 'good' ? 'Healthy' : reputation.health === 'warning' ? 'Watch' : 'At Risk'}
                  </Tag>
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <Text style={{ fontSize: 12, color: token.colorTextSecondary, display: 'block' }}>Bounce Rate</Text>
                    <Text style={{ fontSize: 22, fontWeight: 700, color: reputation.bounceRate > reputation.thresholds.bounce ? '#dc2626' : reputation.bounceRate > 2 ? '#d97706' : '#16a34a' }}>
                      {reputation.bounceRate}%
                    </Text>
                    <Text style={{ fontSize: 10, color: token.colorTextTertiary, display: 'block' }}>limit {reputation.thresholds.bounce}%</Text>
                  </Col>
                  <Col span={8}>
                    <Text style={{ fontSize: 12, color: token.colorTextSecondary, display: 'block' }}>Complaint Rate</Text>
                    <Text style={{ fontSize: 22, fontWeight: 700, color: reputation.complaintRate > reputation.thresholds.complaint ? '#dc2626' : '#16a34a' }}>
                      {reputation.complaintRate}%
                    </Text>
                    <Text style={{ fontSize: 10, color: token.colorTextTertiary, display: 'block' }}>limit {reputation.thresholds.complaint}%</Text>
                  </Col>
                  <Col span={8}>
                    <Text style={{ fontSize: 12, color: token.colorTextSecondary, display: 'block' }}>Duplicates Prevented</Text>
                    <Text style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{(reputation.duplicatesPrevented || 0).toLocaleString()}</Text>
                    <Text style={{ fontSize: 10, color: token.colorTextTertiary, display: 'block' }}>by dedup guard</Text>
                  </Col>
                </Row>
                {reputation.trend?.length > 1 && (
                  <div style={{ height: 90, marginTop: 14 }}>
                    <Text style={{ fontSize: 11, color: token.colorTextTertiary }}>Bounce rate — last 14 days</Text>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={reputation.trend} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
                        <XAxis dataKey="day" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} />
                        <YAxis tick={{ fontSize: 9 }} width={30} />
                        <Tooltip formatter={(v) => `${v}%`} />
                        <Line type="monotone" dataKey="bounceRate" stroke="#dc2626" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Charts + Recent */}
      <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
        {campaigns.length > 0 && (
          <Col xs={24} lg={16}>
            <Card
              title={<Text strong style={{ fontSize: 15 }}>Campaign Activity</Text>}
              style={{ borderRadius: 14, border: `1px solid ${token.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            >
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: '1px solid #f1f5f9', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                    formatter={(v, n, p) => [p.payload.status, 'Status']}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {chartData.map((entry, idx) => (
                      <Cell key={idx} fill={STATUS_COLOR[entry.status] || '#6366f1'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Legend */}
              <Space style={{ marginTop: 12, flexWrap: 'wrap' }}>
                {Object.entries(STATUS_COLOR).map(([s, c]) => (
                  <Space key={s} size={4}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: c }} />
                    <Text style={{ fontSize: 12, color: token.colorTextSecondary, textTransform: 'capitalize' }}>{s}</Text>
                  </Space>
                ))}
              </Space>
            </Card>
          </Col>
        )}
        <Col xs={24} lg={campaigns.length > 0 ? 8 : 24}>
          <Card
            title={<Text strong style={{ fontSize: 15 }}>Quick Stats</Text>}
            style={{ borderRadius: 14, border: `1px solid ${token.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', height: '100%' }}
          >
            {stats ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { label: 'Open Rate', value: stats.emailsSent > 0 ? ((stats.opens / stats.emailsSent) * 100).toFixed(1) + '%' : '—', color: '#9DC533' },
                  { label: 'Click Rate', value: stats.emailsSent > 0 ? ((stats.clicks / stats.emailsSent) * 100).toFixed(1) + '%' : '—', color: '#6366f1' },
                  { label: 'Bounce Rate', value: stats.emailsSent > 0 ? ((stats.bounces / stats.emailsSent) * 100).toFixed(1) + '%' : '—', color: '#ef4444' },
                  { label: 'Avg. per Campaign', value: stats.campaigns > 0 ? Math.round(stats.subscribers / stats.campaigns) : '—', color: '#f59e0b' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: token.colorBgLayout, borderRadius: 10 }}>
                    <Text style={{ color: token.colorTextSecondary, fontSize: 13 }}>{label}</Text>
                    <Text strong style={{ color, fontSize: 15 }}>{value}</Text>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">No data available</Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Campaigns Table */}
      {campaigns.length > 0 && (
        <Card
          title={<Text strong style={{ fontSize: 15 }}>Recent Campaigns</Text>}
          style={{ borderRadius: 14, border: `1px solid ${token.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginTop: 20 }}
        >
          <Table
            dataSource={campaigns}
            columns={recentColumns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 780 }}
          />
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
